import { indexedDBManager } from '../utils/indexedDB';
import { syncEngine } from '../utils/syncEngine';
import { generateUniqueRefId } from '../utils/refIdGenerator';
import dayjs from 'dayjs';
import { ONLINE_FIRST_ENTITIES } from '../constants/configs';

export interface DataOperation {
  entity: string;
  action: 'create' | 'update' | 'soft-delete';
  data: any;
}

export interface QueryOptions {
  page?: number;
  limit?: number;
  search?: string;
  searchFields?: string[];
  filters?: Record<string, any>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  fieldMapping?: Record<string, string>;
}

export class LocalDataService {
  async init(): Promise<void> {
    await syncEngine.init();
    await syncEngine.startAutoSync();
  }

  async create(entity: string, data: any, syncPayload?: any): Promise<any> {
    const refId = generateUniqueRefId(entity);
    const itemData = { ...data, refId };

    if (itemData.id) {
      delete itemData.id;
    }

    if (entity === 'task') {
      this.normalizeTaskData(itemData);
    }

    await indexedDBManager.storeLocalData(entity, itemData);

    const payloadToSync = syncPayload ? { ...syncPayload, refId } : itemData;
    await syncEngine.addToSyncQueue(entity, 'create', payloadToSync);

    return itemData;
  }

  async saveOnlineRecord(entity: string, serverData: any): Promise<void> {
    await indexedDBManager.storeLocalData(entity, serverData);
  }

  async createInspectionWithPrices(
    inspectionData: any,
    pricesData: any[]
  ): Promise<{ inspection: any; prices: any[] }> {
    const inspection = await this.create('inspection', inspectionData);

    const pricePromises = pricesData.map((priceData) => {
      const priceWithInspectionRef = { ...priceData, inspectionRefId: inspection.refId };
      return this.create('price', priceWithInspectionRef);
    });

    const prices = await Promise.all(pricePromises);

    return { inspection, prices };
  }

  async createLegalVerificationInspection(inspectionData: any): Promise<{ inspection: any }> {
    const inspection = await this.create('inspection', inspectionData);
    return { inspection };
  }

  async update(entity: string, id: string, data: any, syncPayload?: any): Promise<any> {
    const existing = await indexedDBManager.getLocalData(entity, id);

    if (!existing) {
      throw new Error(`${entity} with id ${id} not found`);
    }

    const updatedData = { ...existing.data, ...data };

    if (entity === 'task') {
      this.normalizeTaskData(updatedData);
    }

    await indexedDBManager.updateLocalData(entity, id, updatedData);
    if (!ONLINE_FIRST_ENTITIES.has(entity)) {
      const payloadToSync = syncPayload ? { ...syncPayload, refId: id } : updatedData;
      await syncEngine.addToSyncQueue(entity, 'update', payloadToSync);
    }

    return updatedData;
  }

  async delete(entity: string, id: string): Promise<void> {
    const existing = await indexedDBManager.getLocalData(entity, id);
    if (!existing) {
      throw new Error(`${entity} with id ${id} not found`);
    }

    const updatedData = {
      ...existing.data,
      isActive: false,
      updateTime: Date.now(),
    };

    await indexedDBManager.updateLocalData(entity, id, updatedData);

    const minimalUpdateData = {
      refId: existing.data.refId,
      isActive: false,
      updateTime: Date.now(),
    };

    await syncEngine.addToSyncQueue(entity, 'soft-delete', minimalUpdateData);
  }

  private normalizeTaskData(taskData: any): void {
    if (taskData.dueDate) {
      if (typeof taskData.dueDate === 'string') {
        const timestamp = new Date(taskData.dueDate).getTime();
        if (!isNaN(timestamp)) {
          taskData.dueDate = timestamp;
        }
      } else if (typeof taskData.dueDate === 'object' && taskData.dueDate.valueOf) {
        taskData.dueDate = taskData.dueDate.valueOf();
      }
    }
  }

  async get(entity: string, id: string): Promise<any> {
    return await indexedDBManager.getLocalData(entity, id);
  }

  async query(entity: string, options: QueryOptions = {}): Promise<{ data: any[]; total: number }> {
    const { fieldMapping = {} } = options as any;

    const allData = await indexedDBManager.getLocalData(entity);
    let filteredData = allData.map((item: { data: any }) => item.data);
    filteredData = filteredData.filter((item: { isActive: boolean }) => item.isActive !== false);

    if (options.search) {
      filteredData = this.applySearch(filteredData, options.search, options.searchFields);
    }

    if (options.filters) {
      filteredData = this.applyFilters(filteredData, options.filters, fieldMapping);
    }

    if (options.sortBy) {
      filteredData = this.sortResults(filteredData, options.sortBy, options.sortOrder || 'asc');
    }

    const total = filteredData.length;
    const page = options.page || 1;
    const limit = options.limit || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;

    const data = filteredData.slice(startIndex, endIndex);

    return { data, total };
  }

  async sync(): Promise<any> {
    return await syncEngine.forceSync();
  }

  async getSyncStatus(): Promise<any> {
    return await syncEngine.getSyncStatus();
  }

  async getLocalDataById(entity: string, id: string): Promise<any> {
    const data = await indexedDBManager.getLocalData(entity, id);
    return data?.data;
  }

  async getProductByBarcode(barcode: string): Promise<any> {
    const data = await indexedDBManager.getByBarcode('presentation', barcode);
    return data?.data;
  }

  private isDateKey(key: string) {
    return key.toLowerCase().includes('date') || key.toLowerCase().includes('time');
  }

  private isNumericLike(v: any) {
    return typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)));
  }

  private normalizeSearchText(input: unknown): string {
    return String(input ?? '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ') // collapse spaces
      .replace(/[^\p{L}\p{N}\s]/gu, ''); // remove punctuation (optional)
  }

  private flattenToText(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map((v) => this.flattenToText(v)).join(' ');
    }
    if (typeof value === 'object') {
      return Object.values(value)
        .map((v) => this.flattenToText(v))
        .join(' ');
    }
    return String(value);
  }

  private applySearch(data: any[], search: string, fields?: string[]): any[] {
    const q = this.normalizeSearchText(search);
    if (!q) return data;

    return data.filter((item) => {
      const values = fields?.length
        ? fields.map((f) => this.getNestedValue(item, f))
        : Object.values(item);
      const haystack = this.normalizeSearchText(this.flattenToText(values));
      return haystack.includes(q);
    });
  }

  private applyFilters(
    data: any[],
    filters: Record<string, any>,
    fieldMapping?: Record<string, string>
  ): any[] {
    const mapping = fieldMapping || {};
    return data.filter((item) =>
      Object.entries(filters).every(([key, value]) => {
        if (value === undefined || value === null) return true;
        const targetKey = mapping[key] || key;
        const itemVal = this.getNestedValue(item, targetKey);

        if (Array.isArray(value)) {
          if (itemVal === undefined || itemVal === null) return false;
          if (Array.isArray(itemVal)) {
            return value.some((v) => itemVal.map(String).includes(String(v)));
          }
          return value.map(String).includes(String(itemVal));
        }

        if (this.isDateKey(key) && this.isNumericLike(itemVal) && this.isNumericLike(value)) {
          return (
            dayjs(Number(itemVal)).startOf('day').valueOf() ===
            dayjs(Number(value)).startOf('day').valueOf()
          );
        }

        if (this.isNumericLike(itemVal) && this.isNumericLike(value)) {
          return Number(itemVal) === Number(value);
        }
        return itemVal === value;
      })
    );
  }

  private sortResults(data: any[], sortBy: string, sortOrder: 'asc' | 'desc'): any[] {
    return [...data].sort((a, b) => {
      const aValue = this.getNestedValue(a, sortBy);
      const bValue = this.getNestedValue(b, sortBy);

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  async clearLocalData(entity?: string): Promise<void> {
    if (entity) {
      const allData = await indexedDBManager.getLocalData(entity);
      for (const item of allData) {
        await indexedDBManager.deleteLocalData(entity, item.id);
      }
    } else {
      await indexedDBManager.clearSyncQueue();
    }
  }

  async getDatabaseSize(): Promise<number> {
    return await indexedDBManager.getDatabaseSize();
  }
}

export const localDataService = new LocalDataService();
